/**
 * The basket, and how it survives a refresh.
 *
 * There is no client-side cart. The QUOTE is the basket: it holds one item per
 * document, each with its file, print option, binding, cover colour and copy
 * count, and the URL carries only its id (`?q=`). That follows the rule the
 * whole flow is built on — no step may hold data the server does not have —
 * and it means "add another PDF" cannot desynchronise from what will be
 * charged, because there is only one copy of the truth.
 *
 * Adding or editing a document therefore RE-CREATES the quote from its own
 * items plus the change. That is not wasteful: a quote is short-lived, priced
 * server-side from scratch every time, and re-pricing the whole basket is
 * exactly what has to happen anyway — delivery and the free-shipping threshold
 * both depend on every document in it.
 */
import type { DocumentRequest, QuoteOut } from '@/lib/api/types';

/** The quote's items as a request body, ready to send back with a change applied. */
export function documentsFromQuote(quote: QuoteOut | undefined): DocumentRequest[] {
  if (!quote) return [];
  return quote.items.map((item) => ({
    upload_id: item.upload_id,
    print_option_id: item.print_option_id,
    binding_option_id: item.binding_option_id,
    copies: item.copies,
    ...(item.cover_colour ? { cover_colour: item.cover_colour } : {}),
  }));
}

/**
 * The basket with `document` put at the position of the file it names, or
 * appended when that file is not in it yet.
 *
 * Matching on `upload_id` rather than on an index is what makes "go back and
 * change the paper on the second file" work: the customer's position in the
 * flow is not reliable state, but which file they are looking at is.
 */
export function withDocument(
  existing: DocumentRequest[],
  document: DocumentRequest,
): DocumentRequest[] {
  const index = existing.findIndex((entry) => entry.upload_id === document.upload_id);
  if (index === -1) return [...existing, document];

  const next = [...existing];
  next[index] = document;
  return next;
}

/** The basket without the named file. Used by "remove" on the review screen. */
export function withoutDocument(existing: DocumentRequest[], uploadId: string): DocumentRequest[] {
  return existing.filter((entry) => entry.upload_id !== uploadId);
}

/** Everything in the basket except the file being configured right now. */
export function otherDocuments(
  existing: DocumentRequest[],
  uploadId: string | undefined,
): DocumentRequest[] {
  if (!uploadId) return existing;
  return existing.filter((entry) => entry.upload_id !== uploadId);
}
